/**
 * Session Lifecycle — single source of truth for "what state is this session in?"
 *
 * Background: the user app had two different status vocabularies fighting each
 * other — OCPP StatusNotification ships PascalCase ("Preparing", "Charging")
 * while the backend session API returns lowercase ("preparing", "charging").
 * Polling/gap-recovery used to overwrite local PascalCase with backend
 * lowercase, breaking every downstream check (`status === 'Preparing'`).
 *
 * On top of that, the screen the user sees doesn't track OCPP status directly:
 * a session can be "Preparing" on the charger yet already streaming
 * MeterValues — i.e. truly charging. So we keep a separate
 * `lifecycleState` enum that is computed from every signal we have and
 * survives polling refreshes.
 */

/**
 * Canonical lifecycle states for the user-facing flow. These DO NOT need to
 * match either OCPP or the backend — they are the frontend's view of "what
 * should the user see right now?"
 */
export const LIFECYCLE_STATES = Object.freeze({
  IDLE: 'idle',
  WAITING: 'waiting',           // session created, awaiting payment / start
  PREPARING: 'preparing',       // connector plugged, no energy yet
  STARTING: 'starting',         // RemoteStart sent, awaiting StartTransaction
  CHARGING: 'charging',         // energy is flowing (or backend says so)
  STOPPING: 'stopping',         // stop requested, awaiting StopTransaction
  ENDED: 'ended',               // StopTransaction received / session finalized
});

const TERMINAL_LIFECYCLE = new Set([LIFECYCLE_STATES.ENDED]);

const OCPP_PASCAL = new Map([
  ['available', 'Available'],
  ['preparing', 'Preparing'],
  ['charging', 'Charging'],
  ['suspendedev', 'SuspendedEV'],
  ['suspendedevse', 'SuspendedEVSE'],
  ['finishing', 'Finishing'],
  ['finished', 'Finished'],
  ['completing', 'Completing'],
  ['reserved', 'Reserved'],
  ['unavailable', 'Unavailable'],
  ['faulted', 'Faulted'],
  ['occupied', 'Occupied'],
]);

/**
 * Normalize an OCPP/StatusNotification value to canonical PascalCase. Backend
 * APIs and webhook payloads occasionally serialise these as lowercase or
 * snake_case; without normalisation the strict `status === 'Preparing'`
 * checks elsewhere silently fail.
 */
export function normalizeOcppStatus(raw) {
  if (raw == null) return null;
  const key = String(raw).trim().toLowerCase().replace(/[_\s-]/g, '');
  return OCPP_PASCAL.get(key) || raw;
}

/**
 * Backend session.status values (lowercase) that imply the OCPP transaction
 * is in flight — used as the authoritative "the charger is doing something"
 * signal during rehydration / polling.
 */
const BACKEND_ACTIVE_STATUSES = new Set([
  'preparing',
  'starting',
  'remote_start_requested',
  'active',
  'charging',
]);

const BACKEND_TERMINAL_STATUSES = new Set([
  'completed', 'finalized', 'stopped',
  'failed', 'voided', 'expired', 'cancelled',
]);

export function isBackendActiveStatus(status) {
  if (!status) return false;
  return BACKEND_ACTIVE_STATUSES.has(String(status).toLowerCase());
}

export function isBackendTerminalStatus(status) {
  if (!status) return false;
  return BACKEND_TERMINAL_STATUSES.has(String(status).toLowerCase());
}

/**
 * Returns true if there is any first-hand evidence that the charger is alive
 * and delivering energy for this session.
 *
 * This is the rule that overrides everything else: even if the backend says
 * "preparing" and the OCPP StatusNotification says "Preparing", if MeterValues
 * are arriving the charger has decided that it IS charging.
 *
 * @param {object} live - state.live shape from SessionContext
 * @param {number} [opts.recencyMs=120000] - meter value freshness window
 */
export function hasActiveMeterActivity(live, opts = {}) {
  if (!live) return false;
  const recencyMs = opts.recencyMs ?? 2 * 60 * 1000;

  if (live.hasReceivedMeterValues === true) return true;
  if (typeof live.energyKWh === 'number' && live.energyKWh > 0) return true;

  const lastReadingAt = live.lastMeterValueAt || live.lastReadingAt;
  if (lastReadingAt) {
    const t = new Date(lastReadingAt).getTime();
    if (Number.isFinite(t) && Date.now() - t <= recencyMs) return true;
  }

  if (
    typeof live.meter_start === 'number' &&
    typeof live.cumulativeMeter === 'number' &&
    live.cumulativeMeter > live.meter_start
  ) {
    return true;
  }

  return false;
}

/**
 * Compute the canonical lifecycle state from every signal the app has.
 *
 * Precedence (highest first):
 *   1. Local terminal — endTime set or live.lifecycleState already ended
 *   2. Backend terminal — status in BACKEND_TERMINAL_STATUSES
 *   3. Active meter activity (MeterValues received / recent / energy > 0)
 *      → CHARGING regardless of any other status
 *   4. Local tx_id + startTime → CHARGING
 *   5. OCPP status Charging / SuspendedEV / SuspendedEVSE → CHARGING
 *   6. OCPP status Preparing → PREPARING
 *   7. Backend status starting / remote_start_requested → STARTING
 *   8. Backend status preparing / active → PREPARING
 *   9. fall through → WAITING
 *
 * @param {object} params
 * @param {object} params.live - state.live (frontend)
 * @param {object} [params.backendSession] - optional backend session
 */
export function deriveLifecycleState({ live = {}, backendSession = null } = {}) {
  if (live.endTime) return LIFECYCLE_STATES.ENDED;
  if (live.lifecycleState && TERMINAL_LIFECYCLE.has(live.lifecycleState)) {
    return live.lifecycleState;
  }

  if (backendSession?.status && isBackendTerminalStatus(backendSession.status)) {
    return LIFECYCLE_STATES.ENDED;
  }

  // Active charger evidence — wins over every other signal short of "ended".
  if (hasActiveMeterActivity(live)) return LIFECYCLE_STATES.CHARGING;

  // Local state has tx_id + startTime → charging proven from StartTransaction
  if (live.tx_id && live.startTime) return LIFECYCLE_STATES.CHARGING;

  // Frontend OCPP status (PascalCase) wins for in-progress signals
  const ocpp = normalizeOcppStatus(live.status);
  if (ocpp === 'Charging' || ocpp === 'SuspendedEV' || ocpp === 'SuspendedEVSE') {
    return LIFECYCLE_STATES.CHARGING;
  }
  if (ocpp === 'Preparing') return LIFECYCLE_STATES.PREPARING;
  if (ocpp === 'Finishing' || ocpp === 'Completing') return LIFECYCLE_STATES.STOPPING;

  // Backend status fallback
  const backendStatus = String(backendSession?.status || '').toLowerCase();
  if (backendStatus === 'charging' || backendStatus === 'active') {
    return LIFECYCLE_STATES.CHARGING;
  }
  if (backendStatus === 'starting' || backendStatus === 'remote_start_requested') {
    return LIFECYCLE_STATES.STARTING;
  }
  if (backendStatus === 'preparing') return LIFECYCLE_STATES.PREPARING;
  if (backendStatus === 'stopping') return LIFECYCLE_STATES.STOPPING;
  if (backendStatus === 'authorized' || backendStatus === 'pending') {
    return LIFECYCLE_STATES.WAITING;
  }

  return live.lifecycleState || LIFECYCLE_STATES.WAITING;
}

/**
 * Should the UI hide the plug-in / preparing screen and jump to monitoring?
 *
 * Returns true if the session is past the "waiting for connector" stage —
 * either the lifecycle has advanced or MeterValues are flowing.
 */
export function shouldShowChargingScreen(live, backendSession = null) {
  if (!live) return false;
  if (live.endTime) return false; // session ended, summary instead
  if (hasActiveMeterActivity(live)) return true;

  const lc = deriveLifecycleState({ live, backendSession });
  return (
    lc === LIFECYCLE_STATES.PREPARING ||
    lc === LIFECYCLE_STATES.STARTING ||
    lc === LIFECYCLE_STATES.CHARGING ||
    lc === LIFECYCLE_STATES.STOPPING
  );
}

/**
 * Pick the "winning" status when local OCPP-derived status and backend-derived
 * status disagree. Local wins when:
 *   - MeterValues have been seen locally (we have firsthand evidence)
 *   - Local is Charging/Preparing/SuspendedEV* (OCPP active)
 * Otherwise backend (normalized to PascalCase) wins.
 */
export function pickAuthoritativeStatus({ local, backend, live }) {
  const localPascal = normalizeOcppStatus(local);
  const backendPascal = normalizeOcppStatus(backend);

  if (hasActiveMeterActivity(live)) {
    return localPascal === 'Charging' || localPascal === 'Preparing'
      ? localPascal
      : (localPascal || 'Charging');
  }

  if (
    localPascal === 'Charging' ||
    localPascal === 'SuspendedEV' ||
    localPascal === 'SuspendedEVSE'
  ) {
    return localPascal;
  }

  return backendPascal || localPascal || null;
}
